import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toApiError } from '../api/client';
import { authApi } from '../api/endpoints';
import { useAuth } from '../auth/AuthContext';
import { Checkbox, Field } from '../components/Common';
import { InlineError, InlineSuccess } from '../components/States';

/** The partner portal is a separate application, reached by its own URL. */
const PARTNER_WEB_URL = import.meta.env.VITE_PARTNER_WEB_URL || 'http://localhost:5175';

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const returnTo = params.get('returnTo');

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      await login(emailOrPhone.trim(), password);
      navigate(returnTo || '/');
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sf-auth">
      <section className="sf-auth__visual" aria-label="Find your place with RoomKart">
        <div className="sf-auth__brand">RoomKart</div>
        <div className="sf-auth__message">
          <span className="sf-auth__eyebrow">YOUR NEXT CHAPTER STARTS HERE</span>
          <h1>Find a place that feels like yours.</h1>
          <p>Discover stays and homes, made easier with RoomKart.</p>
        </div>
        <img src="/roomkart-customer-login.svg" alt="" className="sf-auth__image" />
      </section>

      <section className="sf-auth__form" aria-label="Sign in to your RoomKart account">
        <div className="sf-card sf-card-pad">
          <h2 className="h4 mb-2">Welcome back</h2>
          <p className="text-muted mb-4">Sign in to continue your search.</p>

          <InlineError message={error} />

          <form onSubmit={submit} noValidate>
            <Field
              label="Email or mobile number"
              htmlFor="login-identity"
              required
            >
              <input
                id="login-identity"
                className="form-control"
                value={emailOrPhone}
                onChange={(e) => setEmailOrPhone(e.target.value)}
                autoComplete="username"
                required
              />
            </Field>

            <Field label="Password" htmlFor="login-password" required>
              <input
                id="login-password"
                type="password"
                className="form-control"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </Field>

            <button type="submit" className="btn btn-primary w-100" disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <div className="d-flex justify-content-between mt-3 small">
            <Link to="/forgot-password">Forgot password?</Link>
            <Link to="/register">Create an account</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [accountType, setAccountType] = useState<'Customer' | 'Owner'>(
    params.get('type') === 'owner' ? 'Owner' : 'Customer',
  );
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [categories, setCategories] = useState<string[]>(['PgHostel']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const toggleCategory = (value: string, checked: boolean) =>
    setCategories((previous) => (checked ? [...previous, value] : previous.filter((c) => c !== value)));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFieldErrors({});

    if (accountType === 'Owner' && categories.length === 0) {
      setError('Choose at least one category you want to list in.');
      setBusy(false);
      return;
    }

    try {
      await register({
        fullName: fullName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        password,
        accountType,
        businessName: accountType === 'Owner' ? businessName.trim() || undefined : undefined,
        categories: accountType === 'Owner' ? categories : [],
      });

      if (accountType === 'Owner') {
        window.location.assign(PARTNER_WEB_URL);
      } else {
        navigate('/');
      }
    } catch (err) {
      const apiError = toApiError(err);
      setError(apiError.message);
      setFieldErrors(apiError.fieldErrors);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container" style={{ maxWidth: 520 }}>
      <div className="sf-card sf-card-pad">
        <h1 className="h4 mb-3">Create your account</h1>

        <div className="btn-group w-100 mb-3" role="group" aria-label="Account type">
          <button
            type="button"
            className={`btn ${accountType === 'Customer' ? 'btn-primary' : 'btn-outline-secondary'}`}
            aria-pressed={accountType === 'Customer'}
            onClick={() => setAccountType('Customer')}
          >
            I am looking for a place
          </button>
          <button
            type="button"
            className={`btn ${accountType === 'Owner' ? 'btn-primary' : 'btn-outline-secondary'}`}
            aria-pressed={accountType === 'Owner'}
            onClick={() => setAccountType('Owner')}
          >
            I want to list a property
          </button>
        </div>

        <InlineError message={error} />

        <form onSubmit={submit} noValidate>
          <Field label="Full name" htmlFor="register-name" required error={fieldErrors.fullName?.[0]}>
            <input
              id="register-name"
              className="form-control"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              autoComplete="name"
              required
              minLength={2}
            />
          </Field>

          <Field label="Email" htmlFor="register-email" required error={fieldErrors.email?.[0]}>
            <input
              id="register-email"
              type="email"
              className="form-control"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </Field>

          <Field
            label="Mobile number"
            htmlFor="register-phone"
            required
            hint="10-digit Indian mobile number."
            error={fieldErrors.phone?.[0]}
          >
            <input
              id="register-phone"
              type="tel"
              inputMode="numeric"
              className="form-control"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              autoComplete="tel"
              required
            />
          </Field>

          <Field
            label="Password"
            htmlFor="register-password"
            required
            hint="At least 8 characters, with an uppercase letter, a lowercase letter and a digit."
            error={fieldErrors.password?.[0]}
          >
            <input
              id="register-password"
              type="password"
              className="form-control"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
            />
          </Field>

          {accountType === 'Owner' && (
            <>
              <Field
                label="Business or brand name"
                htmlFor="register-business"
                hint="You can change this later, and add more businesses from the portal."
              >
                <input
                  id="register-business"
                  className="form-control"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  maxLength={200}
                />
              </Field>

              <fieldset className="mb-3">
                <legend className="form-label">What will you list?</legend>
                <Checkbox
                  id="cat-pg"
                  label="PG & Hostels"
                  checked={categories.includes('PgHostel')}
                  onChange={(checked) => toggleCategory('PgHostel', checked)}
                />
                <Checkbox
                  id="cat-home"
                  label="Homes for rent"
                  checked={categories.includes('HomeRental')}
                  onChange={(checked) => toggleCategory('HomeRental', checked)}
                />
                <Checkbox
                  id="cat-hotel"
                  label="Hotels & short stays"
                  checked={categories.includes('Hotel')}
                  onChange={(checked) => toggleCategory('Hotel', checked)}
                />
                <div className="form-text">You can add more categories later.</div>
              </fieldset>
            </>
          )}

          <button type="submit" className="btn btn-primary w-100" disabled={busy}>
            {busy ? 'Creating account…' : 'Create account'}
          </button>
        </form>

        <p className="mt-3 mb-0 small">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}

export function ForgotPasswordPage() {
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ message: string; token?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const status = await authApi.forgotPassword(emailOrPhone.trim());
      setResult({ message: status.message, token: status.developmentCode });
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container" style={{ maxWidth: 440 }}>
      <div className="sf-card sf-card-pad">
        <h1 className="h4 mb-3">Reset your password</h1>

        <InlineError message={error} />

        {result ? (
          <>
            <InlineSuccess message={result.message} />

            {/* The API only returns a token in development, where no mail provider is
                configured. It says so in its own message. */}
            {result.token && (
              <div className="alert alert-warning small">
                <strong>Development only.</strong> No email was sent. Use this token to continue:
                <code className="d-block mt-2 text-break">{result.token}</code>
                <Link className="btn btn-sm btn-outline-primary mt-2" to="/reset-password">
                  Continue to reset
                </Link>
              </div>
            )}

            <Link to="/login">Back to sign in</Link>
          </>
        ) : (
          <form onSubmit={submit} noValidate>
            <Field label="Email or mobile number" htmlFor="forgot-identity" required>
              <input
                id="forgot-identity"
                className="form-control"
                value={emailOrPhone}
                onChange={(e) => setEmailOrPhone(e.target.value)}
                required
              />
            </Field>

            <button type="submit" className="btn btn-primary w-100" disabled={busy}>
              {busy ? 'Sending…' : 'Send reset link'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [email, setEmail] = useState(params.get('email') ?? '');
  const [token, setToken] = useState(params.get('token') ?? '');
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      await authApi.resetPassword({ email: email.trim(), token: token.trim(), newPassword });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container" style={{ maxWidth: 440 }}>
      <div className="sf-card sf-card-pad">
        <h1 className="h4 mb-3">Choose a new password</h1>

        <InlineError message={error} />

        {done ? (
          <>
            <InlineSuccess message="Your password has been changed. Taking you to sign in…" />
            <Link to="/login">Sign in now</Link>
          </>
        ) : (
          <form onSubmit={submit} noValidate>
            <Field label="Email" htmlFor="reset-email" required>
              <input
                id="reset-email"
                type="email"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Field>

            <Field label="Reset token" htmlFor="reset-token" required hint="From the email we sent you.">
              <textarea
                id="reset-token"
                className="form-control"
                rows={3}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                required
              />
            </Field>

            <Field label="New password" htmlFor="reset-password" required>
              <input
                id="reset-password"
                type="password"
                className="form-control"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
                minLength={8}
              />
            </Field>

            <button type="submit" className="btn btn-primary w-100" disabled={busy}>
              {busy ? 'Saving…' : 'Change password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
